import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { generatePuzzle, formatTime } from "@/lib/sudoku";
import TalentaCTA from "@/components/TalentaCTA";
import { Puzzle, Trophy, Sparkles, Check, Lightbulb, Save, RotateCcw, Play, Eraser, Medal, ArrowLeft, Timer } from "lucide-react";

const DIFF_LABEL = { mudah: "Mudah", sedang: "Sedang", sulit: "Sulit" };

export default function LatihanOtak() {
  const [screen, setScreen] = useState("menu"); // menu | play
  const [mode, setMode] = useState("beginner");
  const [difficulty, setDifficulty] = useState("mudah");
  const [puzzle, setPuzzle] = useState(null);
  const [solution, setSolution] = useState(null);
  const [current, setCurrent] = useState(null);
  const [sel, setSel] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [hints, setHints] = useState(0);
  const [saved, setSaved] = useState(null);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const timer = useRef(null);

  // leaderboard
  const [lbDiff, setLbDiff] = useState("mudah");
  const [lbPeriod, setLbPeriod] = useState("week");
  const [lb, setLb] = useState(null);

  const loadSaved = () => api.get("/sudoku/saved").then((r) => setSaved(r.data.saved)).catch(() => {});
  const loadLb = (d = lbDiff, p = lbPeriod) =>
    api.get(`/sudoku/leaderboard?difficulty=${d}&period=${p}`).then((r) => setLb(r.data)).catch(() => {});

  useEffect(() => { loadSaved(); }, []);
  useEffect(() => { if (screen === "menu") loadLb(); }, [screen, lbDiff, lbPeriod]);

  // timer
  useEffect(() => {
    if (screen === "play" && !result) {
      timer.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(timer.current);
    }
  }, [screen, result]);

  const startNew = async (diff) => {
    setGenerating(true);
    // allow paint before heavy generation
    await new Promise((res) => setTimeout(res, 30));
    const { puzzle: pz, solution: sol } = generatePuzzle(diff);
    setPuzzle(pz); setSolution(sol); setCurrent(pz.slice());
    setDifficulty(diff); setElapsed(0); setWrong([]); setHints(0); setSel(null);
    setResult(null); setGenerating(false); setScreen("play");
  };

  const resume = () => {
    if (!saved) return;
    setPuzzle(saved.puzzle); setSolution(saved.solution); setCurrent(saved.current.slice());
    setDifficulty(saved.difficulty); setMode(saved.mode); setElapsed(saved.elapsed || 0);
    setWrong([]); setHints(0); setSel(null); setResult(null); setScreen("play");
  };

  const setCell = (val) => {
    if (sel == null || puzzle[sel] !== 0) return;
    const next = current.slice();
    next[sel] = val;
    setCurrent(next);
    setWrong((w) => w.filter((i) => i !== sel));
    if (next.every((v, i) => v === solution[i])) finish(next);
  };

  const finish = async (board) => {
    clearInterval(timer.current);
    const t = elapsed;
    try {
      const { data } = await api.post("/sudoku/complete", { mode, difficulty, time_seconds: t });
      setResult({ ...data, time: t });
      loadLb(difficulty, lbPeriod);
    } catch { setResult({ time: t }); }
  };

  const check = () => {
    const bad = [];
    current.forEach((v, i) => { if (v !== 0 && puzzle[i] === 0 && v !== solution[i]) bad.push(i); });
    setWrong(bad);
    if (bad.length === 0) toast.success("Sejauh ini semua benar! 👍");
    else toast.error(`${bad.length} angka masih keliru (ditandai merah)`);
  };

  const hint = () => {
    if (sel == null || puzzle[sel] !== 0 || current[sel] === solution[sel]) { toast.info("Pilih kotak kosong dulu"); return; }
    const next = current.slice();
    next[sel] = solution[sel];
    setCurrent(next); setHints((h) => h + 1);
    setWrong((w) => w.filter((i) => i !== sel));
    if (next.every((v, i) => v === solution[i])) finish(next);
  };

  const saveGame = async () => {
    try {
      await api.post("/sudoku/save", { mode, difficulty, puzzle, current, solution, elapsed });
      toast.success("Progres tersimpan — bisa dilanjutkan nanti");
      loadSaved();
      setScreen("menu");
    } catch { toast.error("Gagal menyimpan"); }
  };

  // ---------------- MENU ----------------
  if (screen === "menu") {
    return (
      <AppShell>
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-sm font-medium mb-3"><Puzzle className="h-4 w-4" /> Latihan Otak</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">Sudoku</h1>
          <p className="text-[#5C5F5C] mt-2 max-w-2xl">Asah <b>logika</b> & <b>ketelitian</b>-mu sambil bersenang-senang. Selesaikan secepat mungkin dan naik ke puncak <b>Liga TalentaPath</b>!</p>
        </div>

        <TalentaCTA className="mb-6" title="Ingin latihan lebih terarah?" sub="Kelas, latihan soal & TryOut lengkap bareng mentor di Bimbel Talenta." />

        {saved && (
          <div className="clay-card p-5 mb-6 bg-[#F4B942]/10 border-[#F4B942]/40 flex flex-wrap items-center justify-between gap-3" data-testid="sudoku-resume-card">
            <div>
              <p className="font-heading font-bold text-[#1A1C1A]">Lanjutkan permainan tersimpan</p>
              <p className="text-sm text-[#5C5F5C]">{mode === "beginner" ? "Pemula" : "Mahir"} · {DIFF_LABEL[saved.difficulty]} · {formatTime(saved.elapsed || 0)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={resume} data-testid="sudoku-resume-btn" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2"><Play className="h-4 w-4" /> Lanjutkan</button>
              <button onClick={async () => { await api.delete("/sudoku/saved"); setSaved(null); }} className="text-sm text-[#5C5F5C]">Hapus</button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className={`clay-card p-6 cursor-pointer transition-all ${mode === "beginner" ? "ring-2 ring-[#4A6B53]" : ""}`} onClick={() => setMode("beginner")} data-testid="sudoku-mode-beginner">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-5 w-5 text-[#4A6B53]" /><h3 className="font-heading font-bold text-lg text-[#1A1C1A]">Pemula</h3></div>
            <p className="text-sm text-[#5C5F5C]">Baru mau coba Sudoku? Mode ramah dengan tombol <b>Cek</b> & <b>Petunjuk</b>. Tingkat Mudah.</p>
          </div>
          <div className={`clay-card p-6 cursor-pointer transition-all ${mode === "expert" ? "ring-2 ring-[#E25E3E]" : ""}`} onClick={() => setMode("expert")} data-testid="sudoku-mode-expert">
            <div className="flex items-center gap-2 mb-2"><Trophy className="h-5 w-5 text-[#E25E3E]" /><h3 className="font-heading font-bold text-lg text-[#1A1C1A]">Mahir</h3></div>
            <p className="text-sm text-[#5C5F5C]">Sudah terbiasa? Pilih tingkat <b>Mudah</b>, <b>Sedang</b>, atau <b>Sulit</b> dan adu waktu tercepat.</p>
          </div>
        </div>

        <div className="clay-card p-6 mb-10">
          <p className="text-sm font-semibold text-[#1A1C1A] mb-3">{mode === "beginner" ? "Mode Pemula — Tingkat Mudah" : "Pilih tingkat kesulitan"}</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {(mode === "beginner" ? ["mudah"] : ["mudah", "sedang", "sulit"]).map((d) => (
              <button key={d} onClick={() => setDifficulty(d)} data-testid={`sudoku-diff-${d}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${difficulty === d ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#1A1C1A]"}`}>
                {DIFF_LABEL[d]}
              </button>
            ))}
          </div>
          <button onClick={() => startNew(mode === "beginner" ? "mudah" : difficulty)} disabled={generating} data-testid="sudoku-start-btn"
            className="px-7 py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <Play className="h-4 w-4" /> {generating ? "Menyiapkan papan…" : "Mulai Bermain"}
          </button>
        </div>

        {/* Liga / Leaderboard */}
        <div className="clay-card p-6" data-testid="sudoku-leaderboard">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-[#F4B942]" /><h2 className="font-heading font-black text-xl text-[#1A1C1A]">Liga TalentaPath</h2></div>
            <div className="flex items-center gap-2">
              {["mudah", "sedang", "sulit"].map((d) => (
                <button key={d} onClick={() => setLbDiff(d)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${lbDiff === d ? "bg-[#4A6B53] text-white border-[#4A6B53]" : "border-[#E5E0D8] text-[#5C5F5C]"}`}>{DIFF_LABEL[d]}</button>
              ))}
              <span className="w-px h-5 bg-[#E5E0D8] mx-1" />
              {[["week", "Turnamen Minggu Ini"], ["all", "Sepanjang waktu"]].map(([p, l]) => (
                <button key={p} onClick={() => setLbPeriod(p)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${lbPeriod === p ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "border-[#E5E0D8] text-[#5C5F5C]"}`}>{l}</button>
              ))}
            </div>
          </div>

          {lbPeriod === "week" && (
            <div className="mb-4 rounded-xl bg-[#F4B942]/10 border border-[#F4B942]/30 p-4" data-testid="tournament-banner">
              <p className="text-sm text-[#1A1C1A] leading-relaxed"><span className="font-semibold">🏆 Turnamen Mingguan</span> — papan ini di-reset otomatis tiap <b>Senin 00:00 WIB</b>. Catat waktu tercepat dan jadilah juara minggu ini!</p>
              {lb?.last_week_champion && (
                <p className="text-sm text-[#8a6d16] mt-2 inline-flex items-center gap-1.5"><Medal className="h-4 w-4" /> Juara minggu lalu ({DIFF_LABEL[lbDiff]}): <b>{lb.last_week_champion.name}</b> · {formatTime(lb.last_week_champion.time_seconds)}</p>
              )}
            </div>
          )}

          {lb?.entries?.length > 0 ? (
            <div className="space-y-1.5">
              {lb.entries.map((e, i) => (
                <div key={e.user_id} className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${lb.me && e.user_id === lb.me.user_id ? "bg-[#E25E3E]/10 border border-[#E25E3E]/30" : "bg-[#FAF8F5]"}`} data-testid={`lb-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`h-7 w-7 grid place-items-center rounded-full text-xs font-black ${i === 0 ? "bg-[#F4B942] text-[#1A1C1A]" : i === 1 ? "bg-[#C9C4BC] text-white" : i === 2 ? "bg-[#E0955B] text-white" : "bg-[#F0ECE4] text-[#5C5F5C]"}`}>{i < 3 ? <Medal className="h-4 w-4" /> : e.rank}</span>
                    <span className="text-sm font-medium text-[#1A1C1A]">{e.name}</span>
                  </div>
                  <span className="font-heading font-bold text-[#E25E3E] inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" /> {formatTime(e.time_seconds)}</span>
                </div>
              ))}
              {lb.me && lb.me.rank > 20 && (
                <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-[#E25E3E]/10 border border-[#E25E3E]/30 mt-2">
                  <div className="flex items-center gap-3"><span className="h-7 w-7 grid place-items-center rounded-full text-xs font-black bg-[#F0ECE4] text-[#5C5F5C]">{lb.me.rank}</span><span className="text-sm font-medium text-[#1A1C1A]">Kamu</span></div>
                  <span className="font-heading font-bold text-[#E25E3E]">{formatTime(lb.me.time_seconds)}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#5C5F5C] py-6 text-center">Belum ada pemain di papan ini. Jadilah yang pertama! 🏁</p>
          )}
        </div>
      </AppShell>
    );
  }

  // ---------------- PLAY ----------------
  const selRow = sel == null ? -1 : Math.floor(sel / 9);
  const selCol = sel == null ? -1 : sel % 9;
  const counts = {};
  current?.forEach((v) => { if (v) counts[v] = (counts[v] || 0) + 1; });

  return (
    <AppShell>
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setScreen("menu")} className="text-sm text-[#5C5F5C] inline-flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Menu</button>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#5C5F5C]">{mode === "beginner" ? "Pemula" : "Mahir"} · {DIFF_LABEL[difficulty]}</span>
            <span className="font-heading font-black text-lg text-[#1A1C1A] inline-flex items-center gap-1.5" data-testid="sudoku-timer"><Timer className="h-4 w-4 text-[#E25E3E]" /> {formatTime(elapsed)}</span>
          </div>
        </div>

        <div className="grid grid-cols-9 bg-[#1A1C1A] rounded-2xl overflow-hidden border-2 border-[#1A1C1A] shadow-xl" data-testid="sudoku-board">
          {current?.map((v, i) => {
            const r = Math.floor(i / 9), c = i % 9;
            const given = puzzle[i] !== 0;
            const isSel = i === sel;
            const related = selRow === r || selCol === c || (Math.floor(selRow / 3) === Math.floor(r / 3) && Math.floor(selCol / 3) === Math.floor(c / 3));
            const sameVal = v !== 0 && sel != null && current[sel] === v;
            const isWrong = wrong.includes(i);
            return (
              <button key={i} onClick={() => setSel(i)} data-testid={`sudoku-cell-${i}`}
                className={`aspect-square flex items-center justify-center text-lg sm:text-xl font-semibold transition-colors
                  ${c % 3 === 2 && c !== 8 ? "border-r-2 border-r-[#1A1C1A]" : "border-r border-r-[#E5E0D8]"}
                  ${r % 3 === 2 && r !== 8 ? "border-b-2 border-b-[#1A1C1A]" : "border-b border-b-[#E5E0D8]"}
                  ${isWrong ? "bg-[#F7D6CE] text-[#D9381E]" : isSel ? "bg-[#F5C4B0]" : sameVal ? "bg-[#FBEAC4]" : related ? "bg-[#EBF0EC]" : "bg-white"}
                  ${given ? "text-[#1A1C1A]" : "text-[#E25E3E]"}`}>
                {v !== 0 ? v : ""}
              </button>
            );
          })}
        </div>

        {/* number pad */}
        <div className="grid grid-cols-9 gap-1.5 mt-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button key={n} onClick={() => setCell(n)} data-testid={`sudoku-num-${n}`} disabled={counts[n] >= 9}
              className="aspect-square rounded-xl bg-white border border-[#E5E0D8] font-heading font-bold text-lg text-[#1A1C1A] hover:border-[#E25E3E] hover:bg-[#E25E3E]/5 transition-colors disabled:opacity-30">
              {n}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button onClick={() => setCell(0)} data-testid="sudoku-erase" className="px-4 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#1A1C1A]/5 transition-colors"><Eraser className="h-4 w-4" /> Hapus</button>
          {mode === "beginner" && (
            <>
              <button onClick={check} data-testid="sudoku-check-btn" className="px-4 py-2.5 rounded-full bg-[#4A6B53] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"><Check className="h-4 w-4" /> Cek</button>
              <button onClick={hint} data-testid="sudoku-hint-btn" className="px-4 py-2.5 rounded-full bg-[#F4B942] text-[#1A1C1A] text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#e5ad35] transition-colors"><Lightbulb className="h-4 w-4" /> Petunjuk{hints > 0 ? ` (${hints})` : ""}</button>
            </>
          )}
          <button onClick={saveGame} data-testid="sudoku-save-btn" className="px-4 py-2.5 rounded-full border border-[#E5E0D8] text-sm font-medium text-[#5C5F5C] inline-flex items-center gap-1.5 hover:border-[#1A1C1A] transition-colors"><Save className="h-4 w-4" /> Simpan</button>
          <button onClick={() => startNew(difficulty)} data-testid="sudoku-new-btn" className="px-4 py-2.5 rounded-full border border-[#E5E0D8] text-sm font-medium text-[#5C5F5C] inline-flex items-center gap-1.5 hover:border-[#1A1C1A] transition-colors"><RotateCcw className="h-4 w-4" /> Papan Baru</button>
        </div>
      </div>

      {result && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sudoku-complete-modal">
          <div className="clay-card bg-white p-8 max-w-sm w-full text-center">
            <div className="h-16 w-16 rounded-2xl bg-[#F4B942] text-[#1A1C1A] grid place-items-center mx-auto mb-4"><Trophy className="h-8 w-8" /></div>
            <h2 className="font-heading font-black text-2xl text-[#1A1C1A] mb-1">Selesai! 🎉</h2>
            <p className="text-[#5C5F5C] mb-4">Waktu kamu: <b className="text-[#E25E3E]">{formatTime(result.time)}</b> · Tingkat {DIFF_LABEL[difficulty]}</p>
            {result.rank && <p className="text-sm text-[#1A1C1A] mb-1">Peringkat Liga: <b>#{result.rank}</b> dari {result.total_players} pemain</p>}
            {result.is_new_best && <p className="text-sm font-semibold text-[#4A6B53] mb-1">🏅 Rekor pribadi baru!</p>}
            <div className="flex items-center gap-2 justify-center mt-5">
              <button onClick={() => startNew(difficulty)} className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors">Main Lagi</button>
              <button onClick={() => setScreen("menu")} className="px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5 transition-colors">Lihat Liga</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
